import { useEffect, useState } from 'react';
import { useConfirm } from '../components/confirm.js';
import { Sheet } from '../components/sheet.js';
import { APP_ROLES, ROLE_LABELS, ROLE_SUMMARIES } from '@mononoke/shared';
import { api, call } from '../platform/api.js';
import type { AppRole, TeamMember } from '../../../api/src/catalog/users.js';
import type { PendingInvite } from '../../../api/src/catalog/invites.js';

/**
 * MANAGE USERS — who is in this workspace, what they may do, and turning that off.
 *
 * ⚠️ IT CANNOT CREATE AN ACCOUNT, AND THE SCREEN SAYS SO RATHER THAN HIDING IT. Supabase
 * Auth owns `auth.users`; this app owns the tenant and the role. Creating an account here
 * would need the `service_role` key inside the API container — a credential that bypasses
 * RLS and can rewrite every table and every auth account — which was weighed and declined
 * (maintainer, 1 Aug 2026).
 *
 * So the normal door is an INVITATION (Guardrail 6: the invitation is what grants access), and
 * the fallback — when email is not working — asks for the account's existing UUID, with the hint
 * saying exactly where to get it. A screen that pretended to create the account and then
 * produced a person who cannot sign in would be the worse answer by a distance.
 *
 * ⚠️ ONE PERSON, ONE SHEET (0.1.509; maintainer, 15 Sep 2026: "go with the tap in the row, 1
 * person sheet"). The row IS the door: tapping it opens the person's sheet with everything about
 * them — name, email, role, second factor — and the two acts, Revoke (reversible) then Delete
 * (permanent, last, absent on your own row). This REPLACES the per-row ⋯ popover of 0.1.239–0.1.508
 * (DESIGN.md rule 5: acts live in one sheet, never per-row menus) and the inline rename beside
 * the avatar. The maintainer had read the three-entry menu on their OWN row — Delete is absent
 * there by design — as "we lost half of the functionality"; the sheet shows the same set to
 * everyone, with the reason a control is absent said in words where it would have been.
 *
 * ⚠️ THERE IS A DELETE, AND IT IS THE SECOND ACT, NOT THE FIRST (0.1.409, maintainer ruling
 * 27 Aug 2026). `movements.actor_id` and `item_edits.actor_id` are ON DELETE RESTRICT by design,
 * because history that says "somebody" is worth less than history that says who. The ruling was
 * that a person must always be deletable — as garments and boxes already are — and, asked what
 * should become of their trace, that the entries AND the name both stay. Migration 077 is how:
 * the movement and edit rows survive, the link is cut, and the name is written into the row on
 * the way out.
 *
 * ⚠️ EMAIL IS EDITABLE HERE SINCE 0.1.509, AND THE SHEET SAYS WHAT THAT CHANGES. The address on
 * the seat is the WORKSPACE'S record (the row, the export, the invitation duplicate check); the
 * sign-in address lives on the Supabase account, out of this API's reach. Changing one does not
 * change the other, and a person who reads only the field must still know that — so the note
 * sits beside the field, not in a doc.
 */

/**
 * ⚠️ THE LABELS MOVED TO `packages/shared` IN 0.1.209 and this array is now a projection of
 * them, not a second copy. The invitation mail has to name a role in words too, and an API that
 * called `data_entry` something different from this screen would disagree in the two places a
 * person compares: the picker an admin chose from, and the message the invitee received.
 */
const ROLES: { value: AppRole; label: string; what: string }[] = APP_ROLES.map((value) => ({
  value,
  label: ROLE_LABELS[value],
  what: ROLE_SUMMARIES[value],
}));

const roleLabel = (role: AppRole): string => ROLE_LABELS[role] ?? role;

/**
 * The 34px avatar's letters (redesign 7b). First + last initial, or the first two of a
 * single-word name — a projection of the name already on the row, never stored, so it
 * cannot drift from it.
 */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** How an invitation's derived state reads. `open` is the only one that is not a dead end. */
const INVITE_STATE_LABELS: Record<PendingInvite['state'], string> = {
  open: 'Invited',
  expired: 'Invitation expired',
  used: 'Accepted',
  revoked: 'Withdrawn',
};

/**
 * ⚠️ AN INSTANT, RENDERED IN THE READER'S OWN ZONE — not a calendar date, so the date helper
 * and its lint rule do not apply here (they guard deriving a `YYYY-MM-DD` from UTC, which lands
 * on tomorrow in a Brussels evening). An invitation expires 72 hours after it was issued, at a
 * moment, and the admin needs to know which evening that is where they are standing.
 */
function whenExpires(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function UsersScreen(): JSX.Element {
  const [users, setUsers] = useState<TeamMember[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [statusLine, setStatusLine] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmEl, ask] = useConfirm();
  const [adding, setAdding] = useState(false);

  /**
   * THE PERSON SHEET (0.1.509): which row is open. Derived from the LIVE list below, so after a
   * mutation the sheet shows the server's row rather than a copy taken when it opened. The two
   * drafts are the sheet's own; they are re-seeded when a different person opens.
   */
  const [personId, setPersonId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const openPerson = (member: TeamMember): void => {
    setProblem(null);
    setStatusLine(null);
    setDraftName(member.fullName);
    setDraftEmail(member.emailAddress);
    setPersonId(member.id);
  };
  const closePerson = (): void => setPersonId(null);

  const [newId, setNewId] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<AppRole>('view_only');

  /**
   * INVITATIONS — `docs/self-service-accounts.md` stage 2.
   *
   * `null` means "not read yet"; `available` false means the database has no migration 046 and
   * the controls are ABSENT rather than broken (§6). The two are different states and the
   * screen must not collapse them: an admin on a database without 046 should see the old
   * add-by-UUID path and no invite door, not a door that answers with an error.
   */
  const [invites, setInvites] = useState<PendingInvite[] | null>(null);
  const [invitesAvailable, setInvitesAvailable] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<AppRole>('view_only');
  /** The pending invitation whose sheet is open (Send again / Withdraw), by id. */
  const [inviteFor, setInviteFor] = useState<string | null>(null);
  /**
   * ⚠️ THE LINK IS SHOWN ONLY WHEN THE MESSAGE DID NOT GO — §9 C. The API returns the token
   * exactly in that case, so this is null on every successful send and there is no state in
   * which a working invitation's credential sits on an admin's screen.
   */
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);

  /**
   * ONE WAY OUT OF THE INVITE SHEET, and every route takes it — Cancel, the scrim, Escape, the
   * Android back gesture, and a successful send. Hoisted the moment it had a second caller: a
   * close that clears the fields down one path and leaves them down another is how a half-typed
   * address comes back on the next open and gets sent to the wrong person.
   */
  const closeInvite = (): void => {
    setInviting(false);
    setInviteEmail('');
    setInviteName('');
  };

  useEffect(() => {
    void (async () => {
      const result = await call('listUsers', () => api.listUsers.query());
      if (result.ok) setUsers(result.data);
      else setProblem(result.failure.message);
    })();
    void (async () => {
      const result = await call('listInvites', () => api.listInvites.query());
      // A failed read here does NOT take the team list down with it: the two are separate
      // questions and the screen's main job is still answerable. It leaves the invitations
      // unavailable, which is the same visible state as a database without 046.
      if (result.ok) {
        setInvitesAvailable(result.data.available);
        setInvites(result.data.invites);
      }
    })();
  }, []);

  /** Every mutation answers with the whole list, re-read in the same transaction, so the
   * screen renders the server's truth rather than patching its own copy. */
  /** A team write. The re-read list IS the confirmation (0.1.523 — no "… ✓" line any more). */
  const run = async (label: string, fn: () => Promise<{ users: TeamMember[] }>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setProblem(null);
    setStatusLine(null);
    const result = await call(label, fn);
    setBusy(false);
    if (!result.ok) {
      setProblem(result.failure.message);
      return false;
    }
    setUsers(result.data.users);
    return true;
  };

  /**
   * AUTO-SAVE AS YOU TYPE (0.1.510; maintainer, 15 Sep 2026: "it needs to auto-save as I type.
   * No save buttons"). 600ms after the last keystroke, whichever of the two drafts differs from
   * the row is written — name first, then email — and the sheet says so. The effect keys on the
   * LIVE row: a finished save updates `users`, the row catches up with the draft, and the effect
   * finds nothing due. A save refused by the server (a duplicate address) leaves the draft
   * standing with the refusal beside it, and nothing re-fires until the draft changes again.
   * `run` drops a call while one is in flight; the row's update after it re-runs this effect,
   * so a keystroke that landed mid-save is not lost.
   */
  useEffect(() => {
    if (personId === null || users === null) return;
    const row = users.find((m) => m.id === personId);
    if (row === undefined) return;
    const name = draftName.trim();
    const email = draftEmail.trim().toLowerCase();
    const nameDue = name !== '' && name !== row.fullName;
    const emailDue = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email !== row.emailAddress;
    if (!nameDue && !emailDue) return;
    const timer = window.setTimeout(() => {
      void (async () => {
        if (nameDue) {
          await run('renameUser', () => api.renameUser.mutate({ userId: row.id, fullName: name }));
        }
        if (emailDue) {
          await run(
            'setUserEmail',
            () => api.setUserEmail.mutate({ userId: row.id, emailAddress: email }),
          );
        }
      })();
    }, 600);
    return () => window.clearTimeout(timer);
    // `run` and `problem` are deliberately not dependencies: a refusal must not re-fire the save.
    // (No react-hooks lint rule is configured here — this is the reasoning, not a suppression.)
  }, [personId, users, draftName, draftEmail]);

  /**
   * The invitation twin of `run`, and it exists rather than reusing it because the answer has a
   * different shape: every invite mutation reports TWO outcomes — was the invitation created,
   * and did the message leave — and §9 C turns on the second being said out loud instead of
   * folded into the first. A `mailed: false` is a SUCCESS that the admin must act on.
   */
  const runInvite = async (
    label: string,
    fn: () => Promise<{ invites: PendingInvite[]; mailed?: boolean; mailProblem?: string | null; token?: string | null }>,
    /** What a MAIL that left says ("Invitation sent to …") — the evidence a send happened. Null
     *  for a write whose only effect is the list (0.1.523 — no plain success lines). */
    said: string | null,
  ): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setProblem(null);
    setStatusLine(null);
    setFallbackLink(null);
    const result = await call(label, fn);
    setBusy(false);
    if (!result.ok) {
      setProblem(result.failure.message);
      return false;
    }
    setInvites(result.data.invites);
    if (result.data.mailed === false) {
      setProblem(result.data.mailProblem ?? 'The invitation was created, but no email was sent.');
      if (typeof result.data.token === 'string') {
        setFallbackLink(`${window.location.origin}/join?token=${encodeURIComponent(result.data.token)}`);
      }
    } else {
      setStatusLine(said);
    }
    return true;
  };

  if (problem !== null && users === null) {
    return (
      <section className="users-screen">
        <p role="alert">{problem}</p>
      </section>
    );
  }
  if (users === null) return <p role="status">Loading…</p>;

  const person = personId === null ? null : (users.find((m) => m.id === personId) ?? null);
  const pending = (invites ?? []).filter((i) => i.state === 'open' || i.state === 'expired');
  const openInvite = inviteFor === null ? null : (pending.find((i) => i.id === inviteFor) ?? null);
  /** A sheet is up: the page-level live regions yield to the copy inside it (one announcement). */
  const sheetUp = inviting || person !== null || openInvite !== null;

  /** The two live regions, rendered wherever the person is looking — inside an open sheet, else
   *  at the top of the page — never in both places at once. */
  const notices = (
    <>
      {statusLine !== null && <p role="status">{statusLine}</p>}
      {problem !== null && (
        <p className="invite-sheet-problem" role="alert">
          {problem}
        </p>
      )}
    </>
  );

  return (
    <section className="users-screen">
      {confirmEl}
      {!sheetUp && notices}
      {/**
        * ⚠️ THE ONE PLACE AN INVITATION LINK IS EVER SHOWN, and it appears only after a send
        * failed (§9 C). Rendered as selectable text rather than behind a "Copy" button on
        * purpose: the clipboard API is permission-gated and fails silently in some installed-PWA
        * contexts, and a copy that quietly did nothing would leave an admin pasting an empty
        * invitation to somebody who is waiting for it.
        */}
      {fallbackLink !== null && (
        <p className="invite-fallback">
          <small>Send this link to them yourself — it works once, and it expires:</small>
          <br />
          <code>{fallbackLink}</code>
        </p>
      )}

      {/* THE ROSTER AND THE ROLE CARD, SIDE BY SIDE (redesign 7b) — on a phone the card drops
          under the list. */}
      <div className="users-layout">
        <div className="user-list-wrap">
          <div className="users-people-head">
            <h3 className="settings-section-label">People with access</h3>
            <span className="users-people-count">{users.length}</span>
          </div>

          {/* ⚠️ A GREEN PILL since 0.1.526 (maintainer, 2 Oct 2026: every dashed box becomes a green
              pill; "+ New person", the Add dropped). What follows is the 0.1.509 history.
              ADD SOMEONE — the dotted box AT THE TOP OF THE LIST, full bleed like the rows
              (maintainer, 15 Sep 2026: the floating "+ Invite someone" and the Invitations
              section at the foot were below the fold on first open; "make a dotted line box
              button ... which will be the new plus add someone button"). The rack list's
              `.slot-empty` idiom — one shape for "make a new one". It opens the invitation sheet
              (the normal door, Guardrail 6); on a database without 046 it opens the add-by-UUID
              form instead, which there is the only door. */}
          <button
            type="button"
            className="btn-primary chip user-add-tile"
            disabled={busy}
            onClick={() => {
              setProblem(null);
              setStatusLine(null);
              if (invitesAvailable) setInviting(true);
              else setAdding(true);
            }}
          >
            + New person
          </button>

          <ul className="user-list">
            {users.map((member) => {
              const disabled = member.disabledAt !== null;
              return (
                <li key={member.id} className={`user-row${disabled ? ' user-row-off' : ''}`}>
                  {/* THE ROW IS THE DOOR (0.1.509): one button, the person's sheet. No left
                      state edge on these rows — the maintainer asked for none (15 Sep 2026), a
                      deliberate deviation from rule 6; the "⚠ no 2FA" text still carries
                      Guardrail 8's warning on the line. */}
                  <button type="button" className="user-row-face" onClick={() => openPerson(member)}>
                    <span className="user-avatar" aria-hidden="true">
                      {initials(member.fullName)}
                    </span>
                    <span className="user-identity">
                      <strong>
                        {member.fullName}
                        {member.isSelf && <span className="you-pill">You</span>}
                      </strong>
                      <small>{member.emailAddress}</small>
                      <span className="user-state">
                        <span className="user-role-text">{roleLabel(member.role)}</span>
                        {/* ONLY THE EXCEPTION SPEAKS (rule 6): a revoked account says "No
                            access" — the account still exists, it simply cannot get in. */}
                        {disabled && <span className="user-off">No access</span>}
                        {/* THREE 2FA STATES, BECAUSE THERE HAVE ALWAYS BEEN THREE (0.1.211):
                            `null` is "we cannot tell" and says so. Flagged for admins only —
                            Guardrail 8's own scope. */}
                        {member.totpEnabled === true && <small className="user-2fa">2FA on</small>}
                        {member.totpEnabled === false && member.role === 'admin' && (
                          <small className="user-2fa-missing">⚠ no 2FA</small>
                        )}
                        {member.totpEnabled === null && <small className="user-2fa-unknown">2FA unknown</small>}
                      </span>
                    </span>
                    <span className="user-row-chevron" aria-hidden="true">
                      ›
                    </span>
                  </button>
                </li>
              );
            })}
            {/**
              * PENDING INVITATIONS ARE ROWS IN THE SAME LIST (0.1.509) — greyed, a dashed
              * avatar, "Invited · until …" or "Invitation expired" on the state line. The
              * Invitations section they lived in is gone (maintainer, 15 Sep 2026), and an
              * invitation nobody can see is one nobody can withdraw (G10), so they moved here.
              * Only actionable ones (maintainer, 10 Aug 2026: "Only need to see which
              * invitations are still open"); spent and withdrawn rows live on in the database.
              */}
            {pending.map((invite) => (
              <li key={invite.id} className="user-row user-row-off user-row-invited">
                <button type="button" className="user-row-face" onClick={() => setInviteFor(invite.id)}>
                  <span className="user-avatar" aria-hidden="true">
                    {initials(invite.fullName)}
                  </span>
                  <span className="user-identity">
                    <strong>{invite.fullName}</strong>
                    <small>{invite.emailAddress}</small>
                    <span className="user-state">
                      <span className="user-role-text">{roleLabel(invite.role)}</span>
                      <span className={invite.state === 'expired' ? 'user-off' : undefined}>
                        {INVITE_STATE_LABELS[invite.state]}
                        {invite.state === 'open' && ` · until ${whenExpires(invite.expiresAt)}`}
                      </span>
                    </span>
                  </span>
                  <span className="user-row-chevron" aria-hidden="true">
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {/* SAID ONCE, UNDER THE LIST, RATHER THAN ON EVERY ROW: when the app cannot read
              second-factor state it is a gap in this deployment, not in the team. */}
          {users.some((m) => m.totpEnabled === null) && (
            <p>
              <small>
                This workspace cannot see who has two-factor sign-in — migration <code>048_mfa_status</code> has not been
                applied. Until it is, check Supabase → Authentication → Users. Guardrail 8 asks for 2FA on administrator
                accounts.
              </small>
            </p>
          )}

          {/* The fenced note that explained the acts (7b → 0.1.509) is gone (maintainer, 15 Sep
              2026: "just remove that box of text") — the confirms say what Revoke and Delete do,
              at the moment it matters. */}
        </div>

        {/* THE ROLE REFERENCE CARD (redesign 7b): the definitions BESIDE the roster. The ONLY
            place they live — `ROLE_SUMMARIES` is the same vocabulary the picker and the
            invitation mail use. */}
        <aside className="role-card">
          <h3>What the roles mean</h3>
          <dl>
            {APP_ROLES.map((r) => (
              <div key={r}>
                <dt>{ROLE_LABELS[r]}</dt>
                <dd>{ROLE_SUMMARIES[r]}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>

      {/* THE PERSON SHEET (0.1.509). Fields save one at a time and say so; the role saves on
          pick (the Picker contract); the acts come last, Revoke before Delete, Delete absent on
          your own row with the reason where it would have been. */}
      {person !== null && (
        <Sheet label={`Actions for ${person.fullName}`} onClose={closePerson} className="person-sheet">
          <header className="sheet-header">
            <strong>
              <span className="user-avatar" aria-hidden="true">
                {initials(person.fullName)}
              </span>{' '}
              {person.fullName}
              {person.isSelf && <span className="you-pill">You</span>}
            </strong>
            <button type="button" onClick={closePerson}>
              Close
            </button>
          </header>
          {notices}
          <div className="person-fields">
            {/* Name and email AUTO-SAVE (0.1.510) — the effect above writes them 600ms after the
                last keystroke and the status line says so; there is no Save button to miss. The
                note about what an email edit changes (0.1.509) is gone at the maintainer's ask;
                the fact stands in this file's header and on `setUserEmail`. */}
            <label className="person-field">
              <span className="field-label">Name</span>
              <input
                type="text"
                aria-label={`New name for ${person.fullName}`}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
              />
            </label>
            <label className="person-field">
              <span className="field-label">Email address</span>
              <input
                type="email"
                aria-label={`Email address for ${person.fullName}`}
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
              />
            </label>
            <label className="person-field">
              <span className="field-label">Role</span>
              {/* Keeps its `Role for {name}` accessible name so the lockout guard still drives
                  it; the mutation and the server's refusal are unchanged. Saves on pick. */}
              <select
                aria-label={`Role for ${person.fullName}`}
                value={person.role}
                disabled={busy}
                onChange={(e) => {
                  const role = e.target.value as AppRole;
                  void run(
                    'updateUserRole',
                    () => api.updateUserRole.mutate({ userId: person.id, role }),
                  );
                }}
              >
                {/* The label alone: the summaries ran off a 375px select in the first frame, and
                    the roles card under the list is where they are read in full. */}
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {/* THE TWO ACTS, side by side, both destructive in look (0.1.510) — Revoke is the
              reversible one, Delete the permanent one, and the confirms say which is which. The
              second-factor line that stood above them (0.1.509) is gone at the maintainer's ask;
              the row on the list still carries "⚠ no 2FA" for an administrator (Guardrail 8). */}
          <section className="item-acts-group">
            <h3>Access</h3>
            <p className="item-acts-row">
              <button
                type="button"
                className="btn-danger"
                disabled={busy}
                onClick={() => {
                  /**
                   * The confirm names its exact target AND says what it does not do — the
                   * standing rule for destructive scope. "Nothing is deleted" is the load-bearing
                   * half. Only asked when REVOKING — restoring access needs no gate.
                   */
                  const disabled = person.disabledAt !== null;
                  void (async () => {
                    if (
                      !disabled &&
                      !(await ask({
                        title: `Revoke ${person.fullName}’s access?`,
                        body: 'They can no longer sign in. Nothing is deleted — their check-outs and edits stay in the history, and you can restore access at any time.',
                        confirmLabel: 'Revoke access',
                      }))
                    ) {
                      return;
                    }
                    void run(
                      'setUserDisabled',
                      () => api.setUserDisabled.mutate({ userId: person.id, disabled: !disabled }),
                    );
                  })();
                }}
              >
                {person.disabledAt !== null ? 'Restore access' : 'Revoke access'}
              </button>
              {/**
                * DELETE — the permanent one, beside the reversible act (0.1.409; side by side
                * since 0.1.510).
                *
                * ⚠️ OFFERED ON YOUR OWN ROW TOO (0.1.510; maintainer: "I don't see the delete
                * button"). 0.1.409 withheld it there and 0.1.509 put a sentence in its place;
                * both read as a missing feature. The standing rule settles it: client-side roles
                * RENDER, they do not gate — the SERVER refuses a self-delete (`self_delete`) with
                * its own sentence, and that sentence is what the sheet shows.
                */}
              <button
                type="button"
                className="btn-danger"
                disabled={busy}
                onClick={() => {
                  void (async () => {
                    if (
                      !(await ask({
                        title: `Delete ${person.fullName}?`,
                        body: 'Their seat and their access are removed for good — this cannot be undone. What they did stays: past check-outs and edits keep their place on the garments’ timelines, under their name. Revoking access instead is reversible.',
                        confirmLabel: 'Delete',
                        tone: 'danger',
                      }))
                    ) {
                      return;
                    }
                    if (await run('deleteUser', () => api.deleteUser.mutate({ userId: person.id }))) {
                      closePerson();
                    }
                  })();
                }}
              >
                Delete
              </button>
            </p>
          </section>
        </Sheet>
      )}

      {/* A PENDING INVITATION'S SHEET: what was sent, and the two acts it still allows. Both are
          refused server-side on a spent or withdrawn invitation, which is why only open and
          expired ones are rows at all. */}
      {openInvite !== null && (
        <Sheet label={`Invitation for ${openInvite.fullName}`} onClose={() => setInviteFor(null)} className="person-sheet">
          <header className="sheet-header">
            <strong>{openInvite.fullName}</strong>
            <button type="button" onClick={() => setInviteFor(null)}>
              Close
            </button>
          </header>
          {notices}
          <p className="invite-sheet-note">
            <small>
              {openInvite.emailAddress} · {roleLabel(openInvite.role)} ·{' '}
              {openInvite.state === 'open' ? `invited, link works until ${whenExpires(openInvite.expiresAt)}` : 'the invitation expired'}
              . They only reach anything in this workspace once they accept.
            </small>
          </p>
          <section className="item-acts-group">
            <h3>Invitation</h3>
            <p className="item-acts-row">
              <button
                type="button"
                className="chip"
                disabled={busy}
                onClick={() =>
                  void runInvite(
                    'reissueInvite',
                    () => api.reissueInvite.mutate({ inviteId: openInvite.id }),
                    `A new invitation is on its way to ${openInvite.emailAddress} ✓`,
                  )
                }
              >
                Send again
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={busy}
                onClick={() => {
                  /**
                   * The confirm names its exact target and says what it does NOT do. "Their old
                   * link stops working" is the half somebody needs before pressing, because
                   * withdrawing is how you undo a mistyped address.
                   */
                  void (async () => {
                    if (
                      !(await ask({
                        title: `Withdraw the invitation to ${openInvite.emailAddress}?`,
                        body: 'Their link stops working immediately. Nobody is removed from the workspace — this invitation was never accepted.',
                        confirmLabel: 'Withdraw invitation',
                      }))
                    ) {
                      return;
                    }
                    if (
                      await runInvite(
                        'revokeInvite',
                        () => api.revokeInvite.mutate({ inviteId: openInvite.id }),
                        // The open invitation leaving the list is the confirmation (0.1.523).
                        null,
                      )
                    ) {
                      setInviteFor(null);
                    }
                  })();
                }}
              >
                Withdraw
              </button>
            </p>
          </section>
        </Sheet>
      )}

      {/* THE INVITE SHEET — the path that replaces opening the Supabase dashboard (§1). Opened
          by the dotted box at the top of the list. ⚠️ ABSENT, NOT DISABLED, WHEN 046 IS MISSING
          (§6): the box opens the add-by-UUID form there instead. */}
      {inviting && (
        <Sheet label="Invite someone" onClose={closeInvite} className="invite-sheet">
          <header className="sheet-header">
            <strong>Invite someone</strong>
            <button type="button" onClick={closeInvite}>
              Cancel
            </button>
          </header>
          {/* THE REFUSAL IS SAID INSIDE THE SHEET (G10): the page's own alert would sit behind
              the scrim. `notices` renders here and not at the top while a sheet is up. */}
          {notices}
          <p className="invite-sheet-note">
            <small>
              They receive an email, choose their own password and confirm their address. They only reach anything
              in this workspace once they accept — the invitation is what gives them access, and it expires.
            </small>
          </p>
          <p className="form-grid invite-form">
            <label>
              Email address
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            </label>
            <label>
              Full name
              <input type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
            </label>
            {/* `aria-label` rather than the wrapping <label>'s text — a <label> that CONTAINS its
                select folds the option text into the accessible name. */}
            <span className="field-label">Role</span>
            <label>
              <select
                aria-label="Role for the invited person"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as AppRole)}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label} — {r.what}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || inviteEmail.trim() === '' || inviteName.trim() === ''}
              onClick={() =>
                void runInvite(
                  'createInvite',
                  () =>
                    api.createInvite.mutate({
                      emailAddress: inviteEmail.trim(),
                      fullName: inviteName.trim(),
                      role: inviteRole,
                    }),
                  `Invitation sent to ${inviteEmail.trim()} ✓`,
                ).then((created) => {
                  // ⚠️ CLEARED ON CREATION, NOT ON A SUCCESSFUL SEND. A mail failure still
                  // created the invitation, and leaving the form filled would invite a second
                  // one for the same person. The SHEET closes on the same signal: a mail
                  // failure has a fallback link to show, and that link renders behind this scrim.
                  if (!created) return;
                  closeInvite();
                })
              }
            >
              Send invitation
            </button>
          </p>
          {/* §9 D: `claimUser` stays, because email can break. One sentence says which path is
              the normal one, and this link is the other's door. */}
          <p className="invite-sheet-note">
            <small>
              Email not working?{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  closeInvite();
                  setAdding(true);
                }}
              >
                Add someone who already has an account
              </button>
            </small>
          </p>
        </Sheet>
      )}

      {/**
        * ADDING SOMEBODY BY ACCOUNT ID, and the hint is the feature. The UUID field looks like an
        * odd thing to ask for until you know the account is made in Supabase — so the screen
        * says where to get it rather than leaving somebody to guess and paste the wrong value.
        */}
      {adding && (
        <div className="user-add">
          <h3>Add someone who already has an account</h3>
          <p>
            <small>
              Accounts are created in Supabase (Authentication → Users), not here. Create the person there, then paste
              their <strong>User UID</strong> below. Adding them here is what gives them a role in this workspace.
              {invitesAvailable ? ' Inviting them is usually easier — use this when email is not working.' : ''}
            </small>
          </p>
          <p className="form-grid">
            <label>
              User UID (from Supabase)
              <input type="text" value={newId} onChange={(e) => setNewId(e.target.value)} />
            </label>
            <label>
              Email address
              <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            </label>
            <label>
              Full name
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </label>
            <span className="field-label">Role</span>
            <label>
              <select
                aria-label="Role for the new person"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as AppRole)}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label} — {r.what}
                  </option>
                ))}
              </select>
            </label>
          </p>
          <p>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || newId.trim() === '' || newEmail.trim() === '' || newName.trim() === ''}
              onClick={() =>
                void run(
                  'claimUser',
                  () =>
                    api.claimUser.mutate({
                      userId: newId.trim(),
                      emailAddress: newEmail.trim(),
                      fullName: newName.trim(),
                      role: newRole,
                    }),
                ).then((ok) => {
                  if (!ok) return;
                  setAdding(false);
                  setNewId('');
                  setNewEmail('');
                  setNewName('');
                })
              }
            >
              Add to workspace
            </button>{' '}
            <button type="button" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </p>
        </div>
      )}
    </section>
  );
}
